import { Box, Card, Grid, Typography, Divider, Stack, Chip, Tooltip } from '@mui/material';
import { useTranslate } from 'react-admin';
import XTextField from '../../components/XTextField';
import XDateField from '../../components/XDateField';
import XCurrencyField from '../../components/XCurrencyField';
import IdField from '../../components/IdField';
import { useEffect, useState } from 'react';
import TransferDialog from './TransferDialog';
import { baseUrl, httpClient } from '../../providers/AppDataProvider';

interface TapGridStats {
  rank?: number | string;
  pnl?: number | string;
  vol?: number | string;
  // Decimal string; null when the user has no TapGrid wallet (never played) or the lookup failed
  balance?: string | null;
  depositCount?: number;
  depositVol?: number | string;
  withdrawCount?: number;
  withdrawVol?: number | string;
}

interface UserOverviewCardProps {
  tabActive: string;
  record: any;
}

const UserOverviewCard = ({ record }: UserOverviewCardProps) => {
  const t = useTranslate();
  const [dialogOpen, setDialogOpen] = useState(false)
  const [transactionType, setTransactionType] = useState('')
  const [asset, setAsset] = useState('')
  const [tapGridStats, setTapGridStats] = useState<TapGridStats | null>(null);

  const userId = record?.data?.id;

  useEffect(() => {
    if (!userId) return;
    // Drop the previous user's figures first, so a new user never shows them (e.g. a stale balance)
    setTapGridStats(null);
    let mounted = true;
    httpClient(`${baseUrl}/tap-grid/users/${userId}/stats?period=ALL_TIME`, { method: 'GET' })
      .then(({ json }) => {
        if (mounted) setTapGridStats((json?.data ?? json ?? null) as TapGridStats | null);
      })
      .catch(() => {
        if (mounted) setTapGridStats(null);
      });
    return () => {
      mounted = false;
    };
  }, [userId]);

  if (!record || !record.data) return null;

  const num = (value?: number | string) => Number(value ?? 0);

  const openDialog = ({type, asset}: {type: string, asset: string}) => {
    setDialogOpen(true)
    setTransactionType(type)
    setAsset(asset)
  };

  const InfoItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box sx={{ minHeight: 50 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );

  return (
    <Box>
      <Grid container spacing={3}>
        {/* User Info Card */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, width: '100%', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('module.userInfo.userInfo')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={6} md={4}>
                <InfoItem label={t('module.userInfo.userId')}>
                  <IdField id={record.data.id} sx={{ width: '100%', height: 25 }} />
                </InfoItem>
              </Grid>
              <Grid item xs={6} md={4}>
                <InfoItem label={t('module.userInfo.loginBy')}>
                  <XTextField text={record.data.loginBy} sx={{ width: '100%', height: 25 }} />
                </InfoItem>
              </Grid>
              <Grid item xs={6} md={4}>
                <InfoItem label={t('module.userInfo.registerAt')}>
                  <XDateField value={record.data.registerAt} sx={{ width: '100%' }} />
                </InfoItem>
              </Grid>
              <Grid item xs={6} md={4}>
                <InfoItem label={t('module.userInfo.status')}>
                  {(() => {
                    const statusConfig: Record<
                      string,
                      { label: string; color: 'success' | 'info' | 'warning' | 'error' | 'default' }
                    > = {
                      new_user: { label: 'New User', color: 'info' },
                      active_user: { label: 'Active User', color: 'success' },
                      dormant_user: { label: 'Dormant User', color: 'warning' },
                      at_risk_user: { label: 'At Risk', color: 'error' },
                      lost_user: { label: 'Lost User', color: 'default' },
                      anonymous: { label: 'Anonymous', color: 'default' },
                    };
                    const config = statusConfig[record.data.status] ?? {
                      label: record.data.status?.toUpperCase() ?? '—',
                      color: 'default',
                    };
                    return (
                      <Tooltip
                        title={t(`module.userInfo.statusTooltip.${record.data.status}`, {
                          _: t('module.userInfo.statusTooltip.anonymous'),
                        })}
                        arrow
                      >
                        <Chip label={config.label} color={config.color} size="small" />
                      </Tooltip>
                    );
                  })()}
                </InfoItem>
              </Grid>
              <Grid item xs={6} md={4}>
                <InfoItem label={t('module.userInfo.lifetimeRevenue')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.lifetimeRevenue}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} md={4}>
                <InfoItem label={t('module.userInfo.cashback.cashback')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      value={record.data.cashbackData?.userTierInfo?.claimableCashbackUsd ?? 0}
                    />
                    <Typography color="text.secondary">/</Typography>
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      value={record.data.cashbackData?.userTierInfo?.claimedCashbackUsd ?? 0}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.disabled">
                      Claimable
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      /
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Claimed
                    </Typography>
                  </Box>
                </InfoItem>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Referral Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, width: '100%', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('module.userInfo.referral')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={6} sm={4} md={6}>
                <InfoItem label={t('module.referralRanking.referralCode')}>
                  <Typography fontWeight={700}>
                    {record.data.referral?.referralCode ?? '—'}
                  </Typography>
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={4} md={6}>
                <InfoItem label={t('module.referralRanking.level')}>
                  <Chip
                    label={record.data.referral?.currentLevel ?? '-'}
                    color="primary"
                    size="small"
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={4} md={6}>
                <InfoItem label={t('module.referralRanking.rank')}>
                  <Typography fontWeight={700}>#{record.data.referral?.rank ?? '—'}</Typography>
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={4} md={6}>
                <InfoItem label={t('module.referralRanking.invitedFriends')}>
                  <Typography fontWeight={700}>
                    {record.data.referral?.invitedFriends ?? 0}
                  </Typography>
                </InfoItem>
              </Grid>
              {/* <Grid item xs={6} sm={4} md={3}>
                <InfoItem label={t('module.referralRanking.revenueGenerated')}>
                  <XCurrencyField
                    value={record.data.referral?.revenueGenerated ?? 0}
                    currency={{ symbol: '$', position: 'before' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <InfoItem label={t('module.referralRanking.derivativesVolume')}>
                  <XCurrencyField
                    value={record.data.referral?.derivativesVolumeUsd ?? 0}
                    currency={{ symbol: '$', position: 'before' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <InfoItem label={t('module.referralRanking.memeVolume')}>
                  <XCurrencyField
                    value={record.data.referral?.memeVolumeUsd ?? 0}
                    currency={{ symbol: '$', position: 'before' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <InfoItem label={t('module.referralRanking.transactionVolume')}>
                  <XCurrencyField
                    value={record.data.referral?.transactionVolume ?? 0}
                    currency={{ symbol: '$', position: 'before' }}
                  />
                </InfoItem>
              </Grid> */}
            </Grid>
          </Card>
        </Grid>

        {/* DEX Statistics Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, width: '100%', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('module.userInfo.dexStatistics')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalBuy')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalBuyDex}
                    sx={{ fontSize: '1.1rem' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalSell')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalSellDex}
                    sx={{ fontSize: '1.1rem' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalPnl')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    colorize
                    value={record.data.totalPnlDex}
                    sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.balance')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.balanceDex}
                    sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4} sx={{ cursor: 'pointer' }} onClick={() => openDialog({type: 'deposit', asset: 'dex'})}>
                <InfoItem label={t('module.userInfo.totalDeposit')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalDepositDex}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4} sx={{ cursor: 'pointer' }} onClick={() => openDialog({type: 'withdraw', asset: 'dex'})}>
                <InfoItem label={t('module.userInfo.totalWithdraw')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalWithdrawDex}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.perpsEquity')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.perpsEquity}
                    sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.tradingVolume')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.dexTradingVolume}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.revenue')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.dexRevenue}
                  />
                </InfoItem>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* MEME Statistics Card */}
        {/* <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, width: '100%', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('module.userInfo.memeStatistics')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalBuy')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalBuyMeme}
                    sx={{ fontSize: '1.1rem' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalSell')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalSellMeme}
                    sx={{ fontSize: '1.1rem' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalPnl')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    colorize
                    value={record.data.totalPnlMeme}
                    sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.balance')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.balanceMeme}
                    sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4} sx={{ cursor: 'pointer' }} onClick={() => openDialog({ type: 'deposit', asset: 'meme' })}>
                <InfoItem label={t('module.userInfo.totalDeposit')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalDepositMeme}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4} sx={{ cursor: 'pointer' }} onClick={() => openDialog({ type: 'withdraw', asset: 'meme' })}>
                <InfoItem label={t('module.userInfo.totalWithdraw')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalWithdrawMeme}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.tradingVolume')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.memeTradingVolume}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.revenue')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.memeRevenue}
                  />
                </InfoItem>
              </Grid>
            </Grid>
            
          </Card>
        </Grid> */}

        {/* Predict Statistics Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, width: '100%', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('module.userInfo.predictStatistics')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalBuy')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalBuyPredict}
                    sx={{ fontSize: '1.1rem' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalSell')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalSellPredict}
                    sx={{ fontSize: '1.1rem' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalPnl')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    colorize
                    value={record.data.totalPnlPredict}
                    sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.balance')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.balancePredict}
                    sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4} sx={{ cursor: 'pointer' }} onClick={() => openDialog({ type: 'deposit', asset: 'predict' })}>
                <InfoItem label={t('module.userInfo.totalDeposit')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalDepositPredict}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4} sx={{ cursor: 'pointer' }} onClick={() => openDialog({ type: 'withdraw', asset: 'predict' })}>
                <InfoItem label={t('module.userInfo.totalWithdraw')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.totalWithdrawPredict}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.tradingVolume')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.predictTradingVolume}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.revenue')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={record.data.predictRevenue}
                  />
                </InfoItem>
              </Grid>
            </Grid>
            
          </Card>
        </Grid>

        {/* TapGrid Statistics Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, width: '100%', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('module.userInfo.tapGridStatistics')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.rank')}>
                  <Typography fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                    {tapGridStats?.rank != null ? `#${tapGridStats.rank}` : '—'}
                  </Typography>
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalPnl')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    colorize
                    value={num(tapGridStats?.pnl)}
                    sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.tradingVolume')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={num(tapGridStats?.vol)}
                    sx={{ fontSize: '1.1rem' }}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.balance')}>
                  {tapGridStats?.balance != null ? (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      value={num(tapGridStats.balance)}
                      sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                    />
                  ) : (
                    // body2: the typography XCurrencyField's text renders with (body2 sets its own size and weight,
                    // so the sx above never reaches the text), so the cell does not jump when the value arrives
                    <Typography variant="body2">—</Typography>
                  )}
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalDeposit')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={num(tapGridStats?.depositVol)}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.depositCount')}>
                  <Typography fontWeight={700}>{num(tapGridStats?.depositCount)}</Typography>
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.totalWithdraw')}>
                  <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={num(tapGridStats?.withdrawVol)}
                  />
                </InfoItem>
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <InfoItem label={t('module.userInfo.withdrawCount')}>
                  <Typography fontWeight={700}>{num(tapGridStats?.withdrawCount)}</Typography>
                </InfoItem>
              </Grid>
            </Grid>
          </Card>
        </Grid>

      </Grid>

      <TransferDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        record={record}
        transactionType={transactionType}
        asset={asset}
      />
    </Box>
  );
};

export default UserOverviewCard;
